import { TranslateService } from "@ngx-translate/core";
import { formatLocalizedDateTime } from "../../utils/localized-date.util";
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  AfterViewInit,
} from "@angular/core";
import { Table } from "primeng/table";
import { cloneDeep } from "lodash";
import { CheckboxChangeEvent } from "primeng/checkbox";
import { ProgressSpinnerMode } from "@angular/material/progress-spinner";
import { StateService } from "../../services/state.service";
import {
  myExperimentOverlayOptions,
  sharedExperimentOverlayOptions,
} from "../../constants/studio/studio-constants";
import { DASHBOARD_TYPES } from "../../constants/dashboard/dashboard-landing.config";
import { CatExpertRouterLink } from "../../app-routing-enum";
import { environment } from "../../../environments/environment.qa";
import { MultiSelectionService } from "../../services/multi-selection.service";

@Component({
  selector: "app-experiment-list",
  templateUrl: "./experiment-list.component.html",
  styleUrl: "./experiment-list.component.scss",
})
export class ExperimentListComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild("expTbl") expTbl!: Table;

  @Input() tableColumns: any[] = [];
  @Input() tableData: any[] = [];
  @Input() allData: any[] = [];
  @Input() skeletonLoader: boolean = false;
  @Input() showFilters: boolean = false;
  @Input() paginationFirst: number = 0;
  @Input() pageSize: number = 20;
  @Input() totalPages: number = 1;
  @Input() currentPage: number = 1;
  @Input() totalItems: number = 0;
  @Input() dragDropDisabled: boolean = false;
  @Input() statusOptions = [];
  @Input() moveDialogView: boolean = false;
  needsExtraStatus = ["cloning", "queued"];
  @Input() showAccordion: boolean = false;
  @Input() showToggleAll: boolean = false;
  @Input() isClientSide: boolean = false; // For handling client side actions like sorting, pagination etc..
  @Input() noRowClick: boolean = false; // For handling client side actions like sorting, pagination etc..
  @Input() styleClass: string = "";
  @Input() showPagination: boolean = true;

  // When true, prevent the "Select All" header checkbox from enabling
  // the global "select all across pages" mode; only select rows on the
  // current page. This is used by grids that must restrict selection to
  // the visible page (e.g. when optimizationFlag is enabled).
  @Input() disableAllPagesSelect: boolean = false;

  @Input() dragDropEnabled: boolean = false;
  @Input() scrollFlag: boolean = false;
  @Input() scrollHeight: string = "";
  @Input() isSelectAndExclude: boolean = false;
  @Input() showAddedRowHighlight: boolean = true;
  excludeIds: any = [];
  selectedRows: any = [];
  @Input() excludeSelectAll: boolean = false;
  @Input() clearSelectAll: boolean = false;
  @Input() overrideAllSelected: boolean | null = null;

  @Input() useSelectionService: boolean = false;
  @Input() selectAllAcrossPages: boolean = false;
  @Input() hideFolderCheckbox: boolean = false;
  // Server-side mode only: re-select rows flagged `isChecked` after each data refresh.
  @Input() preserveCheckedRows: boolean = false;

  @Output() rowDropped = new EventEmitter<{
    dragData: any;
    dropData: number;
  }>();
  @Output() emitTableAction = new EventEmitter<any>();
  @Input() accordionTemplate!: TemplateRef<any>;
  @Input() sibilingTableColSpan: number = 0;
  @Input() expandedRows: Set<any> = new Set();
  @Output() rowCheckboxChange = new EventEmitter<any>();
  @Input() readOnly: boolean = false;
  @Input() canMoveSharedItems: boolean = false;

  @Input() lazyLoad: boolean = false;

  @Input() hasImageLibrary: boolean = false;

  @Input() isHeaderHide: boolean = false;
  @Input() hasEmitPaginationEvent: boolean = false;

  @Input() hasGroupBy: boolean = false;
  @Input() enableCreatedByColumn: boolean = false;

  clientFilteredData: any[] = [];

  skeletonData = [{}, {}, {}, {}, {}, {}, {}, {}, {}, {}];
  pagination = {
    limit: 20,
    options: [10, 20, 30, 50, 100],
  };
  first = 1;
  firstCount = 1;
  lastCount = 20;

  dragingData: any = {};

  actionColumnList: any = [];
  overlayDetails: any = {};
  selectedItems: any[] = [];
  orginalData: any = {};
  filterSuggestions: any[] = [];
  currentRoute: string = "";

  experimentNameMaxLength: number = 50;
  mode: ProgressSpinnerMode = "determinate";

  lastSelectedIndex: number | null = null;
  hoveredRow: any = null;

  allSelected: boolean = false;

  sortField: string = "created_at";
  sortOrder: number = -1;

  imageUrlPrefix = environment.imageUrl;

  dashboardTypes = DASHBOARD_TYPES;

  isSharedRoute: boolean = false;

  dateFilterApplied: {
    [key: string]: { isDirty: boolean; dateRange: Date[] } | undefined;
  } = {};
  clientSideSearchFilters: { [key: string]: string } = {};

  // Lazy load properties
  private scrollContainer: HTMLElement | null = null;
  private isLoadingMore: boolean = false;
  private debounceTimeout: any = null;

  constructor(
    public stateService: StateService,
    public multiSelectService: MultiSelectionService,
    private translate: TranslateService,
  ) {}

  private onTableAction(
    key: string | number | boolean | undefined,
    value: unknown,
    callback: string | undefined,
    extra: Record<string, unknown> = {},
  ) {
    const option: Record<string, unknown> = {
      ...extra,
      ...(key !== undefined && {
        key: key === "parent_sku" ? "sku" : key,
      }),
      ...(callback !== undefined && { callBack: callback }),
    };
    this.emitTableAction.emit({ action: option, data: value });
  }

  ngOnInit(): void {
    if (this.useSelectionService) {
      this.multiSelectService.clear();
    }
    this.currentRoute =
      this.stateService.getActiveRouteSegment() ?? CatExpertRouterLink.Studio;

    this.isSharedRoute = this.currentRoute === CatExpertRouterLink.Shared;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.showFilters?.currentValue === false) {
      this.cancelPendingSearch();
    }

    if (changes && changes.allData) {
      this.allData = changes["allData"].currentValue || [];
    }

    if (changes && changes.tableData) {
      this.tableData = changes["tableData"].currentValue || [];

      if (!this.allData?.length) {
        this.allData = [...this.tableData];
      }

      this.skeletonLoader = false;
      this.isLoadingMore = false; // Reset loading flag when new data arrives

      if (
        this.isClientSide &&
        changes["totalItems"] &&
        changes["totalItems"].currentValue !==
          changes["totalItems"].previousValue
      ) {
        this.firstCount = 1; // Reset first count on new data
        this.currentPage = 1; // Reset to first page on new data
      }
      this.handlePaginationData();

      // Re-setup scroll listener if needed (for dynamic data changes)
      if (this.lazyLoad && this.scrollFlag && !this.scrollContainer) {
        setTimeout(() => {
          this.setupScrollListener();
        }, 500);
      }
    }
    if (changes && changes.showAccordion) {
      this.showAccordion = changes["showAccordion"].currentValue;
    }
    if (changes && changes?.dragDropEnabled) {
      this.dragDropEnabled = changes?.dragDropEnabled?.currentValue || false;
    }

    if (changes && changes?.hasGroupBy) {
      this.hasGroupBy = changes?.hasGroupBy?.currentValue || false;
    }

    // For client-side tables, preserve selection across filter/pagination changes.
    // Only clear when the parent loads a completely new dataset (allData change).
    if (
      this.useSelectionService &&
      (this.selectAllAcrossPages || this.isSelectAndExclude)
    ) {
      if (changes?.tableData || changes?.allData) {
        this.syncSelectionUsingService();
      }
    } else if (this.isClientSide) {
      if (changes?.allData) {
        this.clearTableSelection();
      } else {
        this.allSelected = this.computeAllSelected();
      }
    } else {
      if (!this.hasGroupBy) {
        this.clearTableSelection();
        if (this.preserveCheckedRows && changes?.tableData) {
          this.selectedItems = this.tableData.filter(
            (row: any) => row?.isChecked,
          );
          this.allSelected =
            this.tableData.length > 0 &&
            this.selectedItems.length === this.tableData.length;
        }
      }
    }

    //If a parent component is forcing the checkbox state, apply it here
    if (this.overrideAllSelected !== null) {
      this.allSelected = this.overrideAllSelected;
    }

    // clearing table on clear filters
    if (changes && changes.showFilters) {
      if (this.isClientSide) {
        this.resetFiltersClientSide();
      } else {
        this.expTbl?.clear();
      }
    }

    if (this.isSelectAndExclude && this.useSelectionService) {
      if (changes?.excludeSelectAll) {
        const selectAll = !!changes.excludeSelectAll.currentValue;
        if (selectAll !== this.multiSelectService.isAllSelected$()) {
          this.multiSelectService.toggleSelectAll();
        }
        this.syncSelectionUsingService();
      }
    } else if (this.isSelectAndExclude) {
      if (changes && changes.excludeSelectAll) {
        this.clearSelectAndExclude();
        this.excludeSelectAll =
          changes?.excludeSelectAll?.currentValue || false;
        this.allSelected = this.excludeSelectAll;
        if (!this.allSelected) {
          this.toggleAllSelection({ checked: false });
        } else {
          this.toggleAllSelection({ checked: true });
        }
      }
      if (changes && changes.tableData) {
        if (!this.excludeIds?.length && this.isSelectAndExclude) {
          this.allSelected = this.excludeSelectAll;
        }
        if (this.excludeSelectAll) {
          this.toggleAllSelection({ checked: true });
        } else {
          this.clearSelectAndExclude();
        }
      }
    }

    if (changes && changes.hasEmitPaginationEvent) {
      this.hasEmitPaginationEvent =
        changes?.hasEmitPaginationEvent?.currentValue || false;
    }
  }

  handlePaginationData() {
    if (this.isClientSide) {
      const hasFilters =
        Object.keys(this.clientSideSearchFilters).some(
          (k) => !!this.clientSideSearchFilters[k],
        ) ||
        Object.keys(this.dateFilterApplied).some(
          (k) => !!this.dateFilterApplied[k]?.isDirty,
        ) ||
        this.tableColumns.some(
          (col: any) => col.selectedFilterValues?.length > 0,
        );
      const sourceData = hasFilters ? this.clientFilteredData : this.allData;
      this.totalItems = sourceData.length;
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      this.totalPages = Math.ceil(this.totalItems / this.pageSize) || 1;
      this.tableData = sourceData.slice(start, end);
      this.allSelected = this.computeAllSelected();
    }

    if (this.useSelectionService && this.multiSelectService.isAllSelected$()) {
      this.multiSelectService.applySelectAllToPageRows(this.tableData);
    }

    this.firstCount =
      this.currentPage === 1
        ? this.currentPage
        : (this.currentPage - 1) * this.pageSize + 1;
    this.lastCount =
      this.currentPage * this.pageSize < this.totalItems
        ? this.currentPage * this.pageSize
        : this.totalItems;
  }

  toggleSort(column: any) {
    if (this.sortField === column.value) {
      this.sortOrder = this.sortOrder === 1 ? -1 : 1;
    } else {
      this.sortField = column.value;
      this.sortOrder = -1;
    }

    // client-side sorting
    if (this.isClientSide) {
      if (this.tableData && this.sortField) {
        this.allData = [...this.allData].sort((a: any, b: any) =>
          this.compareValues(
            a[this.sortField],
            b[this.sortField],
            this.sortOrder,
          ),
        );
        this.applyClientSideFilters();
      }
      return;
    }

    let sortInput = {
      sort: this.sortField
        ? [
            {
              field: this.sortField,
              direction: this.sortOrder === 1 ? "asc" : "desc",
            },
          ]
        : [],
      page: this.currentPage,
      page_size: this.pageSize,
    };

    this.onTableAction("sort", sortInput, "sortingAction");
  }

  private compareValues(a: any, b: any, sortOrder: number): number {
    const toNumberSafe = (val: any): number | null => {
      if (val === null || val === undefined) return null;
      if (typeof val === "number") {
        return isNaN(val) ? null : val;
      }
      if (typeof val === "string") {
        const s = val.trim();
        if (s === "" || s === "-") return null;
        // remove common thousands separators
        const cleaned = s.replace(/,/g, "");
        const n = Number(cleaned);
        return isNaN(n) ? null : n;
      }
      return null;
    };

    const aNum = toNumberSafe(a);
    const bNum = toNumberSafe(b);

    // Null-like values go last
    if (aNum === null && bNum === null) {
      // If neither parses to a number, fallback to localized string compare
      return (
        String(a ?? "").localeCompare(String(b ?? ""), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * sortOrder
      );
    }
    if (aNum === null) return 1; // push a to bottom
    if (bNum === null) return -1; // push b to bottom

    // Both parsed as numbers -> numeric compare
    if (aNum !== null && bNum !== null) {
      if (aNum === bNum) return 0;
      return sortOrder * (aNum - bNum);
    }

    // Fallback string compare
    return (
      sortOrder *
      String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: "base",
      })
    );
  }

  isSortedColumn(column: any): boolean {
    return this.sortField === column.value;
  }

  isAscending(column: any): boolean {
    return this.isSortedColumn(column) && this.sortOrder === 1;
  }

  isDescending(column: any): boolean {
    return this.isSortedColumn(column) && this.sortOrder === -1;
  }

  /**
   * SKU currently held by the experiment when it differs from the library
   * SKU (library row needs a sync). Null when in sync or not in experiment.
   */
  getPreviousSku(data: any): string | null {
    if (!data?.sku_out_of_sync) {
      return null;
    }
    const experimentSku = data.experiment_sku;
    return experimentSku && experimentSku !== data.sku ? experimentSku : null;
  }

  getString(val: any, column?: any) {
    if (column?.value === "comment" && val === "") {
      return "— no comment —";
    }
    if (column && (column?.value === "status" || column?.value === "type")) {
      // Split camel/Pascal case values (e.g. "SKUMapping" -> "SKU Mapping")
      // so they render as readable words; other values are untouched.
      if (column?.value === "type" && typeof val === "string") {
        val = val.replace(/([a-z])([A-Z])|([A-Z]+)([A-Z][a-z])/g, "$1$3 $2$4");
      }
      let textVal = this.stateService.titleRemoveUnderscore(val);
      if (typeof textVal === "string") {
        return textVal != "" ? textVal : "";
      }
      return;
    }
    if (column && column.value === "experiment_type") {
      if (val === "folder") return "SHARED.FOLDER";
      if (val === "assortment") return "EXPERIMENT_TYPES.ASSORTMENT";
      if (val === "planogram") return "EXPERIMENT_TYPES.PLANOGRAM";
      if (val === "space_assortment") return "EXPERIMENT_TYPES.SPACE_PLANOGRAM";
      if (val === "bulk_plano") return "EXPERIMENT_TYPES.BULK_PLANO";
      return val;
    }
    if (typeof val === "string") {
      return val != "" ? val : "";
    }

    return val != null ? val : "";
  }

  onDragStart(event: DragEvent, data: any) {
    if (this.dragDropDisabled) {
      return;
    }
    this.dragingData = data;
    // Create custom ghost element
    const ghost = document.createElement("div");
    ghost.innerText = data.name; // Customize text
    ghost.className = "experiment-drag-preview"; // Style this in CSS
    document.body.appendChild(ghost);

    // Make it hidden but still usable as drag image
    ghost.style.position = "absolute";
    ghost.style.top = "-1000px";
    ghost.style.left = "-1000px";

    // Use as drag image
    event.dataTransfer?.setDragImage(ghost, 10, 10);

    // Cleanup after short delay
    setTimeout(() => document.body.removeChild(ghost), 0);
  }

  // Helper method to find hover row data to add the style
  onRowDragOver(event: any, rowData: any) {
    if (this.dragDropDisabled) {
      return;
    }
    event.preventDefault(); // Must allow drop
    this.hoveredRow = rowData;
  }

  // Helper method to call drop the selected row on the folder
  onDropRow(event: any, dropRowData: any) {
    if (this.dragDropDisabled) {
      return;
    }
    if (
      dropRowData.experiment_type !== "folder" ||
      dropRowData === this.dragingData
    ) {
      this.dragingData = {};
      this.hoveredRow = null;
      return;
    }
    const draggedRow = this.dragingData;

    this.rowDropped.emit({
      dragData: draggedRow,
      dropData: dropRowData,
    });
    this.hoveredRow = null;
    this.dragingData = {};
  }

  isRowFolderType(row: any): boolean {
    return row.experiment_type === "folder";
  }

  isNonExpandableType(data: any): boolean {
    return (
      data.experiment_type === "folder" ||
      data.experiment_type === "bulk_plano" ||
      data.experiment_type === "assortment"
    );
  }

  toggleOverlay(actionColumn: any, data: any): void {
    const isShared = data?.hasShared;
    const isFolder = data?.experiment_type === "folder";
    const isSharedRoute = this.currentRoute === CatExpertRouterLink.Shared;
    const hasReadOnlyAccess = data?.access_type === "read";
    const isPublished = data?.is_published;

    if (isShared) {
      if (isPublished) {
        this.actionColumnList = cloneDeep(actionColumn.actionList);
      } else {
        if (isSharedRoute) {
          this.actionColumnList = cloneDeep(sharedExperimentOverlayOptions);
        } else {
          this.actionColumnList = cloneDeep(
            sharedExperimentOverlayOptions,
          ).filter((action: any) => action.key !== "publish");
        }
      }
      this.updatePublishActionLabel(isPublished);
    } else if (isPublished) {
      this.actionColumnList = cloneDeep(actionColumn.actionList);
      this.updatePublishActionLabel(isPublished);
    } else {
      this.actionColumnList = cloneDeep(actionColumn.actionList || []);
    }

    if ((isShared || isSharedRoute) && !this.canMoveSharedItems) {
      this.actionColumnList = this.actionColumnList.filter(
        (action: any) => action.key !== "move",
      );
    }

    if (isFolder || hasReadOnlyAccess) {
      this.actionColumnList = this.actionColumnList.filter(
        (action: any) => !["publish"].includes(action.key),
      );
    }

    this.overlayDetails = data;
    this.clearTableSelection();
  }

  toSelectedExperiment(experiment: any, openInNewTab: boolean = false) {
    // If it's a dashboard type, use the specific callback
    if (this.dashboardTypes.includes(experiment?.type)) {
      this.actionCallback(
        { key: "view", callBack: "navigateToDashboard" },
        experiment,
      );
      return;
    }
    let option = {
      label: "STUDIO.OPEN_IN_NEW_TAB",
      key: openInNewTab,
      callBack: "navigateExperiment",
    };
    this.emitTableAction.emit({ action: option, data: experiment });
  }

  experimentAction(option: any) {
    this.emitTableAction.emit({ action: option, data: this.overlayDetails });
  }

  toggleAllSelection(
    event: CheckboxChangeEvent,
    hasBulk: Boolean = false,
  ): void {
    if (hasBulk) {
      this.toggleAllSelectionBulk(event);
      return;
    }
    if (event.checked) {
      if (this.isClientSide) {
        // Add all selectable items from the active dataset (union — preserves cross-filter selection)
        const activeData = this.getClientActiveData();
        const toAdd = activeData.filter(
          (item: any) =>
            (this.isSharedRoute
              ? !item?.isSharedFolder
              : !item?.hasAdded || !item?.hasSynced) &&
            (this.excludeSelectAll
              ? !this.excludeIds.includes(item.sku)
              : true) &&
            !this.selectedItems.some((s) => s.id === item.id),
        );
        this.selectedItems = [...this.selectedItems, ...toAdd];
        this.selectedRows = [...this.selectedItems];
        const selectedSet = new Set(
          this.selectedItems.map((item: any) => item.id),
        );
        this.allData.forEach((item: any) => {
          item.isChecked = selectedSet.has(item.id);
        });
      } else {
        // Server-side: replace selection with current page items
        this.selectedItems = this.tableData.filter(
          (item) =>
            !(this.hideFolderCheckbox && item?.experiment_type === "folder") &&
            (this.isSharedRoute
              ? !item?.isSharedFolder
              : (!item?.hasAdded || !item?.hasSynced) &&
                (this.excludeSelectAll
                  ? !this.excludeIds.includes(item.sku)
                  : true)),
        );
        this.selectedRows = [...this.selectedItems];
        const selectedSet = new Set(
          this.selectedItems.map((item: any) => item.id),
        );
        this.tableData.forEach((item: any) => {
          item.isChecked = selectedSet.has(item.id);
        });
      }
    } else {
      if (this.isClientSide) {
        // Remove only items from the currently active dataset; leave cross-filter selections intact
        const activeIds = new Set(
          this.getClientActiveData().map((item: any) => item.id),
        );
        this.selectedItems = this.selectedItems.filter(
          (item) => !activeIds.has(item.id),
        );
        this.selectedRows = [...this.selectedItems];
        this.allData.forEach((item: any) => {
          item.isChecked = this.selectedItems.some((s) => s.id === item.id);
        });
      } else {
        this.selectedItems = [];
        this.selectedRows = [];
        this.tableData.forEach((item: any) => {
          item.isChecked = false;
        });
      }
    }

    this.excludeIds = this.excludeSelectAll ? this.excludeIds : [];

    this.handleSelectAndExcludeEmit();
    this.emitMultipleRowData(event.checked, true);
  }

  // Handle select and exclude emit
  handleSelectAndExcludeEmit() {
    if (this.isSelectAndExclude) {
      this.onTableAction("select-all", this.selectedRows, undefined, {
        page: this.currentPage,
        enable: this.allSelected,
      });
    }
  }

  // Helper method clear the table selection
  clearTableSelection(resetFlag: boolean = false) {
    if (
      this.useSelectionService &&
      (this.selectAllAcrossPages || this.isSelectAndExclude)
    ) {
      this.multiSelectService.clear();
    }
    this.allSelected = false;
    this.selectedItems = [];
    if (resetFlag) {
      this.selectedRows = [];
      // Reset row-level checkbox state across all data so no stale isChecked flags remain
      const dataToReset = this.isClientSide ? this.allData : this.tableData;
      dataToReset?.forEach((row: any) => {
        row.check_box = false;
        row.isChecked = false;
      });
    }
    this.emitMultipleRowData();
  }

  /** Returns the currently active dataset respecting active client-side filters. */
  private getClientActiveData(): any[] {
    const hasFilters =
      Object.keys(this.clientSideSearchFilters).some(
        (k) => !!this.clientSideSearchFilters[k],
      ) ||
      Object.keys(this.dateFilterApplied).some(
        (k) => !!this.dateFilterApplied[k]?.isDirty,
      ) ||
      this.tableColumns.some(
        (col: any) => col.selectedFilterValues?.length > 0,
      );
    return hasFilters ? this.clientFilteredData : this.allData;
  }

  /**
   * True when every selectable item in the currently active dataset is present
   * in selectedItems. Used to drive the "Select All" header checkbox state.
   */
  private computeAllSelected(): boolean {
    if (!this.isClientSide) return false;
    const activeData = this.getClientActiveData();
    const selectable = activeData.filter((item: any) =>
      this.isSharedRoute
        ? !item?.isSharedFolder
        : !item?.hasAdded || !item?.hasSynced,
    );
    return (
      selectable.length > 0 &&
      selectable.every((item: any) =>
        this.selectedItems.some((s) => s.id === item.id),
      )
    );
  }

  clearSelectAndExclude() {
    this.excludeSelectAll = false;
    this.allSelected = false;
    this.selectedRows = [];
    this.toggleAllSelection({ checked: false });
  }

  isRowSelected(row: any): boolean {
    if (this.isSelectAndExclude) {
      return this.allSelected
        ? true
        : this.excludeSelectAll
          ? !this.excludeIds.includes(row.sku)
          : this.selectedRows.some((r: any) => r.id === row.id);
    } else {
      // For product image library we are using sku as unique identifier instead of id, so checking based on that
      if (this.hasImageLibrary) {
        return this.selectedItems.some((r: any) => r.sku === row.sku); // using `sku` as unique identifier for product image library
      }
      return this.selectedItems.some((r: any) => r.id === row.id); // assuming `id` is unique
    }
  }

  get selectionModel() {
    return this.isSelectAndExclude ? this.selectedRows : this.selectedItems;
  }

  toggleRowSelectionBulk(checked: boolean, row: any): void {
    if (row.isDisabled) return;
    row.check_box = checked;

    this.rowCheckboxChange.emit({
      data: row,
      checked: checked,
    });

    this.emitMultipleRowData();
  }

  toggleRowSelection(event: CheckboxChangeEvent, row: any): void {
    const nativeEvent = event.originalEvent;
    if (nativeEvent) {
      nativeEvent.stopPropagation();
    }

    row.isChecked = !!event.checked;
    this.rowCheckboxChange.emit({
      data: row,
      checked: !!event.checked,
    });

    if (this.isSelectAndExclude) {
      const existingRowIndex = this.selectedRows.findIndex(
        (r: any) => r.id === row.id,
      );
      if (existingRowIndex !== -1) {
        this.selectedRows.splice(existingRowIndex, 1);
      } else {
        this.selectedRows.push(row);
      }

      if (this.excludeSelectAll) {
        let existingIndex = this.excludeIds.findIndex(
          (r: string) => r === row.sku,
        );
        if (existingIndex !== -1) {
          this.excludeIds.splice(existingIndex, 1);
        } else {
          this.excludeIds.push(row.sku);
        }
      }
    } else {
      // For product image library we are using sku as unique identifier instead of id
      let existingIndex: number;
      if (this.hasImageLibrary) {
        existingIndex = this.selectedItems.findIndex((r) => r.sku === row.sku);
      } else {
        existingIndex = this.selectedItems.findIndex((r) => r.id === row.id);
      }
      if (existingIndex !== -1) {
        this.selectedItems.splice(existingIndex, 1);
      } else {
        this.selectedItems.push(row);
      }
    }

    this.allSelected = this.selectedItems.length === this.tableData.length;
    this.handleSelectAndExcludeEmit();
    this.emitMultipleRowData();
  }

  toggleRowSelectionClientSide(
    event: CheckboxChangeEvent,
    row: any,
    column: any = null,
  ) {
    if (column?.hasBulk) {
      this.toggleRowSelectionBulk(event.checked, row);
      return;
    }
    const nativeEvent = event.originalEvent;
    if (nativeEvent) {
      nativeEvent.stopPropagation();
    }

    // Set isChecked from event.checked
    row.isChecked = event.checked;
    this.rowCheckboxChange.emit({
      data: row,
      checked: !!event.checked,
    });

    const existingIndex = this.selectedItems.findIndex((r) => r.id === row.id);

    if (event.checked) {
      // Add to selectedItems if not already present
      if (existingIndex === -1) {
        this.selectedItems.push(row);
      }
    } else {
      // Remove from selectedItems if present
      if (existingIndex !== -1) {
        this.selectedItems.splice(existingIndex, 1);
      }
    }

    // Update allSelected state based on the full active dataset, not just the current page
    this.allSelected = this.computeAllSelected();

    this.emitMultipleRowData(); // Emit updated list
  }

  toggleRowSelectionClientSideBulk(event: CheckboxChangeEvent, row: any) {
    event.originalEvent?.stopPropagation();

    row.isChecked = event.checked;

    const index = this.selectedItems.findIndex((r: any) => r.sku === row.sku);

    if (event.checked && index === -1) {
      this.selectedItems.push(row);
    }

    if (!event.checked && index !== -1) {
      this.selectedItems.splice(index, 1);
    }

    this.emitMultipleRowData();
  }

  toggleRowSelectionUsingService(row: any): void {
    this.multiSelectService.toggleSelectionRow(row);
    this.rowCheckboxChange.emit({
      data: row,
      checked: this.multiSelectService.isSelected(row.id),
    });
    if (this.selectAllAcrossPages || this.isSelectAndExclude) {
      this.syncSelectionUsingService();
    }
  }

  setRowSelectionUsingService(row: any, checked: boolean): void {
    if (this.multiSelectService.isSelected(row.id) !== checked) {
      this.multiSelectService.toggleSelectionRow(row);
    }
    this.syncSelectionUsingService();
  }

  toggleAllSelectionUsingService(): void {
    if (this.selectAllAcrossPages || this.isSelectAndExclude) {
      const checked = !this.isPageAllSelectedUsingService();
      if (this.isSelectAndExclude) {
        if (this.multiSelectService.isAllSelected$()) {
          if (checked) {
            this.tableData
              .filter((row: any) => !this.multiSelectService.isSelected(row.id))
              .forEach((row: any) =>
                this.multiSelectService.toggleSelectionRow(row),
              );
          } else {
            this.multiSelectService.clear();
          }
        } else if (checked) {
          this.multiSelectService.selectAllRows(this.tableData);
        } else {
          this.multiSelectService.deselectAllRows(this.tableData);
        }
        this.syncSelectionUsingService(checked, true);
        this.emitTableAction.emit({
          action: {
            key: "select-all",
            page: this.currentPage,
            enable: checked,
          },
          data: this.selectedRows,
        });
        return;
      }
      if (checked) {
        if (!this.multiSelectService.isAllSelected$()) {
          this.multiSelectService.toggleSelectAll();
        }
        this.tableData
          .filter((row: any) => !this.multiSelectService.isSelected(row.id))
          .forEach((row: any) =>
            this.multiSelectService.toggleSelectionRow(row),
          );
        this.multiSelectService.applySelectAllToPageRows(this.tableData);
      } else {
        this.multiSelectService.clear();
      }
      this.syncSelectionUsingService(checked, true);
      return;
    }
    // Header checkbox now performs page-only selection. Global select-all
    // is controlled by the explicit "Select all X entries" link in the grid.
    const pageRows = this.isClientSide
      ? this.getClientActiveData()
      : this.tableData;

    //clear all selection if all rows on the current page are selected
    if (this.isPageAllSelectedUsingService()) {
      this.multiSelectService.deselectAllRows(pageRows);
      return;
    }

    // If global "select all" mode is active, toggle exclusion for page rows
    if (this.multiSelectService.isAllSelected$()) {
      const selectedCount = pageRows.filter((row: any) =>
        this.multiSelectService.isSelected(row.id),
      ).length;

      if (selectedCount === pageRows.length) {
        // All page rows currently selected -> deselect them by toggling each (adds to excluded)
        pageRows.forEach((row: any) =>
          this.multiSelectService.toggleSelectionRow(row),
        );
      } else {
        // Some page rows excluded -> include them by toggling only the excluded ones (removes from excluded)
        pageRows
          .filter((row: any) => !this.multiSelectService.isSelected(row.id))
          .forEach((row: any) =>
            this.multiSelectService.toggleSelectionRow(row),
          );
      }

      return;
    }

    // Normal (non-global) mode: select/deselect rows only on current page
    const selectedCount = pageRows.filter((row: any) =>
      this.multiSelectService.isSelected(row.id),
    ).length;

    if (selectedCount === pageRows.length) {
      this.multiSelectService.deselectAllRows(pageRows);
    } else {
      this.multiSelectService.selectAllRows(pageRows);
    }

    // Notify parent that header select was used so it can show the "Select all N entries" message
    const pageAllSelected = this.isPageAllSelectedUsingService();
    this.onTableAction("select-all", this.selectedRows, undefined, {
      page: this.currentPage,
      enable: pageAllSelected,
    });
  }

  private syncSelectionUsingService(
    checked?: boolean,
    isAllSelected: boolean = false,
  ): void {
    this.multiSelectService.applySelectAllToPageRows(this.tableData);
    this.tableData.forEach((row: any) => {
      row.isChecked = this.multiSelectService.isSelected(row.id);
    });
    this.selectedItems = this.multiSelectService.getSelectedRows();
    this.selectedRows = [...this.selectedItems];
    this.allSelected = this.isPageAllSelectedUsingService();
    if (this.isSelectAndExclude) {
      this.excludeIds = this.multiSelectService.getExcludedItems();
      this.excludeSelectAll = this.multiSelectService.isAllSelected$();
    }
    this.emitMultipleRowData(checked, isAllSelected);
  }

  // Return true if all rows on the current page are selected via the MultiSelectionService
  isPageAllSelectedUsingService(): boolean {
    if (!this.tableData || !this.tableData.length) return false;
    const result = this.tableData.every((row) =>
      this.multiSelectService.isSelected(row.id),
    );

    return result;
  }

  isExperimentSelectionIndeterminate(): boolean {
    if (!this.tableData?.length) {
      return false;
    }

    if (this.useSelectionService) {
      const selectedCount = this.tableData.filter((row: any) =>
        this.multiSelectService.isSelected(row.id),
      ).length;
      return selectedCount > 0 && selectedCount < this.tableData.length;
    }

    return this.selectedItems.length > 0 && !this.allSelected;
  }

  getRowCheckedState(data: any, column: any): boolean {
    if (this.useSelectionService)
      return this.multiSelectService.isSelected(data.id);
    if (column.hasBulk) return data.check_box;
    if (this.hasGroupBy) return !!data.isChecked;
    if (this.isClientSide) return data.isChecked;
    return this.isRowSelected(data);
  }

  isRowDisabled(data: any): boolean {
    return (
      this.readOnly || (data?.hasAdded === false ? false : data?.hasSynced)
    );
  }

  onRowCheckboxChange(event: any, data: any, column: any): void {
    if (this.useSelectionService) {
      this.toggleRowSelectionUsingService(data);
    } else if (this.isClientSide) {
      this.toggleRowSelectionClientSide(event, data, column);
    } else {
      this.toggleRowSelection(event, data);
    }
  }

  emitMultipleRowData(checked?: boolean, isAllSelected: boolean = false) {
    const option = {
      key: this.selectedItems.length,
      callBack: "multipleActions",
    };

    let excludePayload: any = { exclude_ids: [], exclude: false };
    if (this.isSelectAndExclude) {
      excludePayload.exclude_ids = this.excludeIds;
      excludePayload.exclude = true;
    }

    const emitData = {
      checked: checked,
      action: option,
      data: this.selectedItems,
      rows: this.selectedRows,
      ...(this.isSelectAndExclude && this.excludeSelectAll
        ? excludePayload
        : {}),
    };
    if (isAllSelected) {
      emitData["isAllSelected"] = this.allSelected;
    }
    this.emitTableAction.emit(emitData);
  }

  toggleAllSelectionBulk(event: CheckboxChangeEvent): void {
    const isChecked = event.checked;
    this.allSelected = isChecked;

    this.allData.forEach((item) => {
      if (!item.isDisabled) {
        item.check_box = isChecked;
      }
    });

    this.tableData.forEach((item) => {
      if (!item.isDisabled) {
        item.check_box = isChecked;
      }
    });
    this.rowCheckboxChange.emit({
      isAllSelected: true,
      checked: isChecked,
      currentPageData: this.tableData,
    });
    this.emitMultipleRowData();
  }

  formatHumanReadableDate(dateStr: string): string {
    // Validate input
    if (!dateStr || typeof dateStr !== "string") {
      return "";
    }

    const inputDateObj = new Date(dateStr + "Z");

    // Check if the date is valid
    if (isNaN(inputDateObj.getTime())) {
      return "";
    }

    const now = new Date();

    const inputDay = new Date(
      inputDateObj.getFullYear(),
      inputDateObj.getMonth(),
      inputDateObj.getDate(),
    );
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const relative =
      inputDay.getTime() === today.getTime()
        ? "today"
        : inputDay.getTime() === yesterday.getTime()
          ? "yesterday"
          : null;
    return formatLocalizedDateTime(inputDateObj, relative);
  }

  enableEditing(experiment: any, event: any) {
    event.stopPropagation();
    this.orginalData = cloneDeep(experiment);
    experiment.isEditable = true;
  }

  disableEditing(experiment: any, event: any) {
    event.stopPropagation();
    experiment.isEditable = false;
    experiment.name = this.orginalData.name;
    event.target.value = "";
  }

  editExperimentAction(experiment: any, event: any) {
    event.stopPropagation();
    if (experiment?.name === this.orginalData.name) {
      this.disableEditing(experiment, event);
      return;
    }
    if (
      !event.target.value &&
      experiment?.name.length > this.experimentNameMaxLength
    ) {
      return;
    }
    this.onTableAction("edit", experiment, "renameExperiment");
    experiment.isEditable = false;
    event.target.value = "";
  }

  findFilterOptions(event: any, filterOptions: any) {
    if (event) {
      this.filterSuggestions = filterOptions.filter((each: any) => {
        if (each.value.toLowerCase().includes(event.query.toLowerCase())) {
          return each;
        }
      });
    }
  }

  clearAutocompleteFilters() {
    this.expTbl.clear();
  }

  onNgSelectChange(selectedItems: any[], column: any) {
    const selectedValues = Array.isArray(selectedItems)
      ? selectedItems.filter((v) => v !== undefined && v !== null)
      : [];

    this.onTableAction(
      column.value,
      column.value === "published_status"
        ? selectedValues[0] ?? ""
        : selectedValues,
      "filterAction",
    );
  }

  onNgSelectChangeClientSide(_selectedItems: any[], _column: any) {
    // column.selectedFilterValues is already updated by applyDropdownFilter;
    // delegate to the unified filter pipeline.
    this.applyClientSideFilters();
  }

  filterDropdownOptions(column: any, searchValue: any = "") {
    const options = column?.filterOptions || [];
    const searchTerm = searchValue?.toString().trim().toLowerCase() || "";
    const selectedValues = new Set(
      Array.isArray(column?.selectedFilterValues)
        ? column.selectedFilterValues
        : [],
    );

    if (!searchTerm) {
      column.filteredFilterOptions = options;
      return;
    }

    column.filteredFilterOptions = options.filter((option: any) => {
      const rawLabel = option?.label?.toString() || "";
      const translatedLabel = rawLabel
        ? this.translate.instant(rawLabel)?.toString().toLowerCase() || ""
        : "";
      const label = rawLabel.toLowerCase();
      const value = option?.value?.toString().toLowerCase() || "";
      return (
        selectedValues.has(option?.value) ||
        label.includes(searchTerm) ||
        translatedLabel.includes(searchTerm) ||
        value.includes(searchTerm)
      );
    });
  }

  normalizeDropdownFilterSelection(column: any) {
    if (column) {
      column.selectedFilterValues = Array.isArray(column.selectedFilterValues)
        ? column.selectedFilterValues.filter(
            (value: any) => value !== undefined && value !== null,
          )
        : [];
    }
  }

  applySingleDropdownFilter(value: any, column: any) {
    column.selectedFilterValues = value == null ? [] : [value];
    this.applyDropdownFilter(column);
  }

  applyDropdownFilter(column: any) {
    this.normalizeDropdownFilterSelection(column);
    const selectedValues = column?.selectedFilterValues || [];

    if (
      this.areDropdownFilterValuesEqual(
        selectedValues,
        column?.appliedFilterValues,
      )
    ) {
      column.filterSearchText = "";
      column.filteredFilterOptions = null;
      return;
    }

    if (this.isClientSide) {
      this.onNgSelectChangeClientSide(selectedValues, column);
    } else {
      this.onNgSelectChange(selectedValues, column);
    }

    column.appliedFilterValues = [...selectedValues];
    column.filterSearchText = "";
    column.filteredFilterOptions = null;
  }

  private areDropdownFilterValuesEqual(
    currentValues: any[] = [],
    appliedValues: any[] = [],
  ) {
    const normalize = (values: any[]) =>
      (Array.isArray(values) ? values : [])
        .filter((value: any) => value !== undefined && value !== null)
        .map((value: any) => value.toString())
        .sort();

    const current = normalize(currentValues);
    const applied = normalize(appliedValues);

    return (
      current.length === applied.length &&
      current.every((value: string, index: number) => value === applied[index])
    );
  }

  filterSearch(value: string, column: any) {
    this.cancelPendingSearch();
    this.debounceTimeout = setTimeout(() => {
      this.debounceTimeout = null;
      if (!this.showFilters) {
        return;
      }
      this.onTableAction(column.value, value, "filterSearchAction");
    }, 300);
  }

  private cancelPendingSearch(): void {
    if (this.debounceTimeout !== null) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }
  }

  filterSearchClientSide(value: string, column: any) {
    const searchValue = value?.toString().trim().toLowerCase() || "";
    const key = column?.value;

    if (searchValue) {
      this.clientSideSearchFilters[key] = searchValue;
    } else {
      delete this.clientSideSearchFilters[key];
    }

    this.applyClientSideFilters();
  }

  resetFiltersClientSide() {
    this.tableColumns.forEach((item: any) => {
      if (item.filterType === "dropdown" && item?.selectedFilterValues) {
        item.selectedFilterValues = [];
        item.appliedFilterValues = [];
      }
    });
    this.clientSideSearchFilters = {};
    // this.clientFilteredData = [];
    this.currentPage = 1;
    // this.clearTableSelection(true);
    this.handlePaginationData();
  }

  private matchesClientSideSearch(
    item: any,
    key: string,
    searchValue: string,
  ): boolean {
    const normalizedSearch = searchValue?.toString().trim().toLowerCase() || "";
    if (!normalizedSearch) {
      return true;
    }

    const cellValue = String(item?.[key] ?? "")
      .toLowerCase()
      .trim();

    // Allow comma-separated exact matching for store_id (e.g. "1001, 1002").
    if (["store_id", "id", "sku", "sku_id"].includes(key)) {
      const storeIds = normalizedSearch
        .split(",")
        .map((value: string) => value.trim())
        .filter((value: string) => !!value);

      if (storeIds.length > 1 || normalizedSearch.includes(",")) {
        return storeIds.includes(cellValue);
      }
    }

    return cellValue.includes(normalizedSearch);
  }

  applyClientSideFilters() {
    let filtered = [...this.allData];

    // Text search filters
    for (const key of Object.keys(this.clientSideSearchFilters)) {
      const search = this.clientSideSearchFilters[key];
      if (search) {
        filtered = filtered.filter((item: any) =>
          this.matchesClientSideSearch(item, key, search),
        );
      }
    }

    // Dropdown filters (selectedFilterValues on each column)
    for (const col of this.tableColumns) {
      if (col.selectedFilterValues?.length) {
        filtered = filtered.filter((item: any) =>
          col.selectedFilterValues.includes(item?.[col.value]),
        );
      }
    }

    // Date range filters
    for (const key of Object.keys(this.dateFilterApplied)) {
      const df = this.dateFilterApplied[key];
      if (df?.isDirty && df.dateRange?.length === 2) {
        const [from, to] = df.dateRange;
        filtered = filtered.filter((item: any) => {
          const d = new Date(item?.[key]);
          return d >= from && d <= to;
        });
      }
    }

    this.clientFilteredData = filtered;
    this.currentPage = 1;
    // Selection is intentionally preserved across filter changes.
    // allSelected is recomputed against the new active dataset.
    this.handlePaginationData();
  }

  updatePagination() {
    if (this.isClientSide) {
      this.handlePaginationData();
      if (this.hasEmitPaginationEvent) {
        this.handlePaginationEmitAction();
      }
    } else {
      this.handlePaginationEmitAction();
    }
  }

  onPageChange(event: any) {
    this.allSelected = false;
    this.first = event.first;
    this.pageSize = event.rows;
    this.updatePagination();
  }

  goToPreviousPage() {
    if (this.currentPage > 1) {
      this.currentPage = this.currentPage - 1;
      this.updatePagination();
    }
  }

  goToNextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage = this.currentPage + 1;
      this.updatePagination();
    }
  }

  onPageInputChange(event: any) {
    const inputPage = parseInt(event.target.value, 10);
    if (!isNaN(inputPage) && inputPage >= 1 && inputPage <= this.totalPages) {
      this.currentPage = inputPage;
      this.first = (inputPage - 1) * this.pageSize;
      this.updatePagination();
    }
  }

  handlePaginationEmitAction() {
    const paginationInput = {
      page: this.currentPage,
      page_size: this.pageSize,
    };

    this.onTableAction("pagination", paginationInput, "paginationAction");
  }

  // Helper method to get the StatusOptions to update the status
  getDropdownOptions(dataStatus: string): any[] {
    if (this.isClientSide) return this.statusOptions;

    const isExtraStatus = this.needsExtraStatus.includes(dataStatus);
    const exists = this.statusOptions.some(
      (opt: any) => opt.value === dataStatus,
    );

    if (isExtraStatus && !exists) {
      // Add the missing status temporarily to the options
      return [
        {
          label: this.formatLabel(dataStatus),
          value: dataStatus,
        },
        ...this.statusOptions,
      ];
    }

    return this.statusOptions;
  }

  // Helper method to format the status label
  formatLabel(value: string): string {
    return this.stateService.titleRemoveUnderscore(value) || value;
  }

  statusUpdate(event: any, rowData: any) {
    this.onTableAction("status", rowData, "experimentStatusAction");
  }

  actionCallback(action: any, rowData: any) {
    this.emitTableAction.emit({ action: action, data: rowData });
  }

  getOwnerName(ownerBy: any) {
    return ownerBy ? ownerBy?.first_name + " " + ownerBy?.last_name : "";
  }

  toggleAccordionRow(row: any, event: Event) {
    event.stopPropagation();
    if (this.expandedRows.has(row)) {
      this.expandedRows.delete(row);
    } else {
      this.expandedRows.add(row);
      this.onTableAction("toggle-accordion", row, "getPlansForExperiment");
    }
  }

  isRowExpanded(row: any): boolean {
    return this.expandedRows.has(row);
  }

  expandAllRows() {
    // Expand all expandable rows (exclude folders, bulk_plano, assortment) in UI only
    this.tableData.forEach((row: any) => {
      if (!this.isNonExpandableType(row)) {
        this.expandedRows.add(row);
      }
    });
  }

  collapseAllRows() {
    // Collapse all rows
    this.expandedRows.clear();
  }

  toggleAllRows() {
    if (this.areAllRowsExpanded()) {
      this.collapseAllRows();
    } else {
      // Get all expandable experiments (exclude folders, bulk_plano, assortment)
      const expandableExperiments = this.tableData.filter(
        (row: any) => !this.isNonExpandableType(row),
      );

      // Emit event to parent to fetch plans for all experiments
      if (expandableExperiments.length > 0) {
        this.onTableAction(
          "expand-all",
          expandableExperiments,
          "expandAllExperiments",
        );
      }
    }
  }

  hasExpandableRows(): boolean {
    return this.tableData.some((row: any) => !this.isNonExpandableType(row));
  }

  areAllRowsExpanded(): boolean {
    const expandableRows = this.tableData.filter(
      (row: any) => !this.isNonExpandableType(row),
    );
    return (
      expandableRows.length > 0 &&
      expandableRows.every((row: any) => this.expandedRows.has(row))
    );
  }

  getImageUrl(variantKey: string, variantsData: any): string {
    const imageData = variantsData?.[variantKey];
    const imagePath = imageData?.front || imageData?.side || imageData?.top;

    return this.imageUrlPrefix + imagePath;
  }

  getSkuImageUrl(rowData: any): string {
    const defaultImageUrl =
      "https://cdn.catexpert.ab-inbev.com/assets/images/image-placeholder.svg";

    // Check variant-specific image
    if (rowData.sku_image) {
      return rowData.sku_image;
    }

    // Fallback to placeholder
    return defaultImageUrl;
  }

  onSkuImageError(event: Event) {
    // If image load fails fallback to placeholder
    (event.target as HTMLImageElement).src =
      "https://cdn.catexpert.ab-inbev.com/assets/images/image-placeholder.svg";
  }

  getValueColor(value: any, column: any, index: number): string {
    const customColor = column.colorValues?.[index];
    if (customColor) return customColor;

    // Fallback logic: Green if > 0, else Red
    const numericValue = Number(value);
    if (numericValue >= 0) {
      return "#6EBC14"; // Green
    } else {
      return "#DC3545"; // Red
    }
  }

  getAbsoluteValue(value: any): number | string {
    if (typeof value === "string") {
      // Remove + or - sign manually
      const updatedValue = value.replace(/^-/, "");
      return updatedValue;
    }
    return Math.abs(value);
  }

  titleAddUnderscore(value: string): string {
    return value ? value.replace(/\s+/g, "_").toLowerCase() : "";
  }

  // Helper method to decide whether to show the status dropdown
  showStatusDroupdownColumn(data: any, column: any): boolean {
    return (
      !this.moveDialogView &&
      data?.access_type !== "read" &&
      data?.experiment_type !== "folder" &&
      column?.value === "status" &&
      !data?.is_published
    );
  }

  // Helper method to decide whether to show the status text for read only(shared) and published experiments
  showStatusTextColumn(data: any, column: any): boolean {
    return (
      ((this.moveDialogView ||
        data?.access_type === "read" ||
        data?.is_published) &&
        data?.experiment_type !== "folder" &&
        column?.value === "status") ||
      column?.value === "processed_status"
    );
  }

  // Helper method to update the publish action label based on the published status of the experiment
  updatePublishActionLabel(isPublished: boolean): void {
    this.actionColumnList.forEach((action: any) => {
      if (action.key === "publish") {
        action.label = isPublished ? "STUDIO.UNPUBLISH" : "STUDIO.PUBLISH";
      }
    });
  }
  toggleRadioSelectionBulk(selectedRow: any) {
    this.tableData.forEach((row) => {
      row.check_box = row.id === selectedRow.id ? row.id : null;
    });

    this.selectedItems = [selectedRow];
    this.emitMultipleRowData();
  }

  trackByKey(index: number, item: any) {
    return item.key;
  }

  ngAfterViewInit(): void {
    // Set up scroll listener for lazy loading
    if (this.lazyLoad && this.scrollFlag) {
      setTimeout(() => {
        this.setupScrollListener();
      }, 500);
    }
  }

  ngOnDestroy(): void {
    // Clean up scroll listener
    this.removeScrollListener();

    this.cancelPendingSearch();
  }

  private setupScrollListener(): void {
    // Get the scrollable container from PrimeNG table
    const tableElement = this.expTbl?.el?.nativeElement;
    if (tableElement) {
      this.scrollContainer =
        tableElement.querySelector(".p-datatable-scrollable-body") ||
        tableElement.querySelector(".p-datatable-wrapper");

      if (this.scrollContainer) {
        this.scrollContainer.addEventListener(
          "scroll",
          this.onScroll.bind(this),
          { passive: true },
        );
      }
    }
  }

  private removeScrollListener(): void {
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener(
        "scroll",
        this.onScroll.bind(this),
      );
      this.scrollContainer = null;
    }
  }

  private onScroll(event: Event): void {
    if (!this.lazyLoad || this.isLoadingMore) {
      return;
    }

    const target = event.target as HTMLElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    // Check if user has scrolled to the bottom (with a small threshold)
    const threshold = 10; // pixels from bottom
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - threshold;

    if (isAtBottom) {
      // Check if there are more items to load
      const currentItemsCount = this.tableData?.length || 0;
      if (currentItemsCount < this.totalItems) {
        this.isLoadingMore = true;
        this.loadMoreData();
      }
    }
  }

  private loadMoreData(): void {
    // Increment page and emit lazy load event
    this.currentPage++;

    const paginationInput = {
      page: this.currentPage,
      page_size: this.pageSize,
    };

    this.onTableAction("pagination", paginationInput, "paginationAction");

    // Reset loading flag after a short delay
    setTimeout(() => {
      this.isLoadingMore = false;
    }, 1000);
  }

  hasPartialSelected(): boolean {
    return !!this.tableData.find((row: any) => row.__hasPartialChildSelection);
  }
  // Helper method to close and validate selected date range for date filter
  handleClickOutside(event: any, field: string) {
    if (!this.dateFilterApplied[field]?.isDirty) {
      event.value = [];
    } else {
      event.value = this.dateFilterApplied[field]?.dateRange;
    }
  }

  // Helper method to disable date filter's "apply" btn if no date selected
  isDatesSelected(dateRange: Date[]) {
    const validDateRange = dateRange?.filter((e: Date) => e != null);
    return !dateRange || (validDateRange && validDateRange.length < 1)
      ? true
      : false;
  }

  resetCalendar(
    field: string,
    calendar?: {
      value?: Date[];
      hideOverlay?: () => void;
      updateInputfield?: () => void;
    },
  ) {
    this.updateDateFilter(field, false, []);
    this.clearDateFilterFromTableState(field);
    this.onTableAction(field, [], "filterByDate");
    if (calendar) {
      calendar.value = [];
      calendar.updateInputfield?.();
      calendar.hideOverlay?.();
    }
    this.blurActiveElement();
  }

  resetCalendarClientSide(
    field: string,
    calendar?: {
      value?: Date[];
      hideOverlay?: () => void;
      updateInputfield?: () => void;
    },
  ) {
    this.updateDateFilter(field, false, []);
    this.recomputeClientSideFilteredData();
    if (calendar) {
      calendar.value = [];
      calendar.updateInputfield?.();
      calendar.hideOverlay?.();
    }
    this.blurActiveElement();
  }

  // Helper method to track if date filter is applied
  updateDateFilter(field: string, isDirty: boolean, dateRange: Date[]) {
    this.dateFilterApplied[field] = {
      isDirty,
      dateRange,
    };
  }

  private blurActiveElement(): void {
    const activeElement = document.activeElement as HTMLElement | null;
    activeElement?.blur();
  }

  private clearDateFilterFromTableState(field: string): void {
    if (!this.expTbl?.filters) {
      return;
    }

    const filterState = this.expTbl.filters[field] as any;
    if (!filterState) {
      return;
    }

    if (Array.isArray(filterState)) {
      filterState.forEach((constraint: any) => {
        if (
          constraint &&
          typeof constraint === "object" &&
          "value" in constraint
        ) {
          constraint.value = null;
        }
      });
      return;
    }

    if (typeof filterState === "object" && "value" in filterState) {
      filterState.value = null;
    }
  }

  private normalizeToMidday(date: Date): Date {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(12, 0, 0, 0);
    return normalizedDate;
  }

  private getDateFilterRange(value: Date[]): { start: Date; end: Date } | null {
    if (!value || value.length === 0) {
      return null;
    }

    if (value.length === 1 || !value[1]) {
      const date = value[0];
      return {
        start: this.normalizeToMidday(
          new Date(date.getFullYear(), date.getMonth(), 1),
        ),
        end: this.normalizeToMidday(
          new Date(date.getFullYear(), date.getMonth() + 1, 0),
        ),
      };
    }

    const startDate = value[0];
    const endDate = value[1];
    return {
      start: this.normalizeToMidday(
        new Date(startDate.getFullYear(), startDate.getMonth(), 1),
      ),
      end: this.normalizeToMidday(
        new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0),
      ),
    };
  }

  private parseDateValue(value: any): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === "string") {
      const hasTimeZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
      const parsedDate = new Date(hasTimeZone ? value : `${value}Z`);
      return isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    const parsedDate = new Date(value);
    return isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  applyDateFilterClientSide(value: Date[], field: string) {
    const dateRange = this.getDateFilterRange(value);
    if (!dateRange) {
      this.resetCalendarClientSide(field);
      return;
    }

    const { start, end } = dateRange;
    this.updateDateFilter(field, true, [start, end]);
    this.recomputeClientSideFilteredData();
    this.blurActiveElement();
  }

  private recomputeClientSideFilteredData() {
    if (!this.isClientSide) {
      return;
    }

    let filteredData = [...this.allData];

    this.tableColumns.forEach((column: any) => {
      if (column.filterType !== "dropdown") {
        return;
      }

      const selectedValues = Array.isArray(column.selectedFilterValues)
        ? column.selectedFilterValues.filter(
            (v: any) => v !== undefined && v !== null,
          )
        : [];

      if (!selectedValues.length) {
        return;
      }

      filteredData = filteredData.filter((item: any) =>
        selectedValues.includes(item?.[column.value]),
      );
    });

    Object.keys(this.clientSideSearchFilters).forEach((key: string) => {
      const searchValue = this.clientSideSearchFilters[key];
      if (!searchValue) {
        return;
      }

      filteredData = filteredData.filter((item: any) =>
        this.matchesClientSideSearch(item, key, searchValue),
      );
    });

    Object.keys(this.dateFilterApplied).forEach((field: string) => {
      const dateFilter = this.dateFilterApplied[field];
      if (!dateFilter?.isDirty || dateFilter.dateRange?.length < 2) {
        return;
      }

      const start = dateFilter.dateRange[0];
      const end = dateFilter.dateRange[1];

      filteredData = filteredData.filter((item: any) => {
        const rawDateValue = item?.[field];
        const parsedDate = this.parseDateValue(rawDateValue);
        if (!parsedDate) {
          return false;
        }

        const normalizedItemDate = this.normalizeToMidday(
          new Date(
            parsedDate.getFullYear(),
            parsedDate.getMonth(),
            parsedDate.getDate(),
          ),
        );

        return normalizedItemDate >= start && normalizedItemDate <= end;
      });
    });

    this.clientFilteredData = filteredData;
    this.totalItems = filteredData.length;
    this.currentPage = 1;
    // Paginate from filtered data
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.totalPages = Math.ceil(this.totalItems / this.pageSize) || 1;
    this.tableData = this.clientFilteredData.slice(start, end);
    this.firstCount = 1;
    this.lastCount = Math.min(this.pageSize, this.totalItems);
    this.onTableAction(
      undefined,
      [...this.clientFilteredData],
      "updateTableFilterOptionsClientSide",
    );
  }

  // Helper method to filter all data based on selected date range
  applyDateFilter(value: Date[], filter: Function, field: string) {
    const dateRange = this.getDateFilterRange(value);
    if (!dateRange) {
      this.updateDateFilter(field, false, []);
      this.clearDateFilterFromTableState(field);
      this.blurActiveElement();
      return;
    }

    const { start, end } = dateRange;
    this.onTableAction(field, [start, end], "filterByDate");
    this.updateDateFilter(field, true, [start, end]);

    this.blurActiveElement();
  }
}
